import type { ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Bell, FileWarning, Plus, Receipt, Settings, Smartphone, WifiOff } from 'lucide-react'
import { Logo } from './Logo'
import { UpdatePrompt } from './UpdatePrompt'
import { useOnline } from '../lib/hooks'
import { cn } from './ui'

const DESKTOP_NAV = [
  { to: '/uredaji', label: 'Uređaji', icon: Smartphone },
  { to: '/racuni', label: 'Računi', icon: Receipt },
  { to: '/racuni/novi', label: 'Dodaj račun', icon: Plus },
  { to: '/reklamacije', label: 'Reklamacije', icon: FileWarning },
  { to: '/podsjetnici', label: 'Podsjetnici', icon: Bell },
  { to: '/postavke', label: 'Postavke', icon: Settings },
]

const MOBILE_NAV = [
  { to: '/uredaji', label: 'Uređaji', icon: Smartphone },
  { to: '/racuni', label: 'Računi', icon: Receipt },
  { to: '/racuni/novi', label: 'Dodaj', icon: Plus, highlight: true },
  { to: '/reklamacije', label: 'Reklamacije', icon: FileWarning },
  { to: '/postavke', label: 'Postavke', icon: Settings },
]

function navActive(pathname: string, to: string) {
  if (to === '/racuni') return pathname === to || (/^\/racuni\/[^n]/.test(pathname) && pathname !== '/racuni/novi')
  return pathname === to || pathname.startsWith(`${to}/`)
}

export function AppLayout({ children }: { children: ReactNode }) {
  const online = useOnline()
  const location = useLocation()
  const focusedFlow = location.pathname === '/racuni/novi' ||
    location.pathname.startsWith('/obrada/') ||
    location.pathname.includes('/reklamacije/nova/')

  return (
    <div className="min-h-dvh bg-sand-100">
      <a href="#glavni-sadrzaj" className="skip-link">Prijeđi na glavni sadržaj</a>
      <div className="mx-auto flex w-full max-w-[1480px]">
        <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 flex-col border-r border-sand-200 bg-white px-4 py-6 lg:flex">
          <div className="px-2 pb-8"><Logo /></div>
          <nav aria-label="Glavna navigacija" className="flex flex-1 flex-col gap-1">
            {DESKTOP_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                aria-current={navActive(location.pathname, item.to) ? 'page' : undefined}
                className={cn(
                  'flex min-h-11 items-center gap-3 rounded-xl px-3 text-[15px] font-semibold transition-colors',
                  navActive(location.pathname, item.to)
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-ink-soft hover:bg-sand-50 hover:text-ink',
                )}
              >
                <item.icon aria-hidden="true" className="h-5 w-5" />
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="rounded-xl bg-sand-50 p-3 text-xs leading-5 text-ink-muted">
            <strong className="block text-ink">Privatno na ovom uređaju</strong>
            Dokumenti se otvaraju samo autoriziranim zahtjevom.
          </div>
        </aside>

        <main id="glavni-sadrzaj" className={cn('min-h-dvh min-w-0 w-full flex-1', focusedFlow ? 'pb-6' : 'pb-28 lg:pb-10')}>
          <div className="flex h-16 items-center border-b border-sand-200 bg-white px-4 lg:hidden"><Logo /></div>
          {!online && (
            <div role="status" className="mx-4 mt-4 flex items-center gap-2 rounded-xl bg-warn-50 px-3 py-2 text-sm text-warn-700 sm:mx-6">
              <WifiOff aria-hidden="true" className="h-4 w-4 shrink-0" />
              Niste povezani. Dodavanje i obrada računa zahtijevaju internetsku vezu.
            </div>
          )}
          <div className="mx-auto w-full max-w-6xl">{children}</div>
        </main>
      </div>

      {!focusedFlow && (
        <nav aria-label="Glavna navigacija" className="mobile-nav lg:hidden">
          <ul className="mx-auto flex max-w-md items-stretch justify-between px-1">
            {MOBILE_NAV.map((item) => (
              <li key={item.to} className="flex-1">
                <NavLink
                  to={item.to}
                  end={item.to === '/racuni' || item.to === '/reklamacije'}
                  className={cn(
                    'relative flex min-h-[64px] flex-col items-center justify-center gap-1 px-0.5 py-2 text-xs font-bold',
                    navActive(location.pathname, item.to) ? 'text-brand-700' : 'text-ink-muted',
                  )}
                >
                  {item.highlight ? (
                    <span className="-mt-7 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-600 text-white shadow-lift">
                      <item.icon aria-hidden="true" className="h-6 w-6" />
                    </span>
                  ) : <item.icon aria-hidden="true" className="h-5 w-5" />}
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
      <UpdatePrompt />
    </div>
  )
}

export function PageHeader({ title, subtitle, action, back, compact = false }: { title: string; subtitle?: string; action?: ReactNode; back?: ReactNode; compact?: boolean }) {
  return (
    <header className={cn('px-4 sm:px-6 sm:pt-9', compact ? 'pt-5' : 'pt-7')}>
      {back}
      <div className={cn('flex items-start justify-between', compact ? 'flex-nowrap gap-3' : 'flex-wrap gap-4')}>
        <div className="min-w-0">
          <h1 className={cn('leading-tight', compact ? 'text-[20px] sm:text-[34px]' : 'text-[28px] sm:text-[34px]')}>{title}</h1>
          {/* U kompaktnom zaglavlju podnaslov bi se na telefonu lomio u tri retka uz akcije, pa se pojavljuje tek kad ima mjesta. */}
          {subtitle && (
            <p className={cn('mt-1 max-w-2xl text-[15px] text-ink-muted', compact && 'hidden sm:block')}>{subtitle}</p>
          )}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    </header>
  )
}

export function Page({ children }: { children: ReactNode }) {
  return <div className="px-4 py-5 sm:px-6 sm:py-6">{children}</div>
}
